/************************************************************
 * Project: Books-App
 * Author: Marcos Santos - A01072792
 * BCIT class Comp2913 - ReactJS by Daniel Takeuchi
 * Date: October, 8, 2020.
 * Description: Assignment - Books-App
 * https://github.com/maasantos10/
 ************************************************************/

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FiBookOpen } from 'react-icons/fi';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import logoImg from '../../assets/bookLogoLibrary.png';

import { useToast } from '../../hooks/toast';
import getValidationErrors from '../../utils/getValidationErros';

import Input from '../../components/Input';
import Button from '../../components/Button';
import ListContent from '../../components/ListContent';
import Pagination from './../../components/Pagination';

import { ScrollContent, Container, Content, AnimationContainer, Background } from './styles';

import api from '../../services/api';

interface SigInFormData {
  book: string;
  
}

interface BookList {
    id: string;
    volumeInfo: {
      title: string,
      previewLink: string,
      description: string,
      imageLinks: { 
                  smallThumbnail: string, 
                  thumbnail: string,
    },
    categories: string,
}
}

let indexPage = 1;
let totalPages = 0;
let currentPage = 1;
let paginationShow = true;


const Search: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  
  const { addToast } = useToast();
  const [bookList, setBookList] = useState<BookList[]>([]);

  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [loadingPage, setLoadingPage] = useState(true);

  const nextPage = useCallback(
    async () => {

      setTotalPage(totalPage);
      setPage(page);
      const book = formRef.current?.getFieldValue('book');

      indexPage = page;
      currentPage++;
      indexPage += 10;
      
      if (currentPage >= Math.floor(totalPages/10)) {
        currentPage = Math.floor(totalPages/10);
      }

      if (indexPage >= (totalPages)) {
        indexPage -= 10;
        addToast({
            type: 'info',
            title: 'You reached the last page',
            description:
              'last page',
        });
      }
      
      api.get(`/v1/volumes?q=${book}&orderBy=newest&maxResults=10&startIndex=${indexPage}`, {
      })
      .then((response) => {
        setBookList(response.data.items);
        totalPages = response.data.totalItems;
      });
    },
    [setPage, setBookList, addToast, setTotalPage, page, totalPage],
  );
  
  /******************* **********************/
  const previousPage = useCallback(
    async () => {
      indexPage -= 10;
      if (indexPage <=0 ) {
        indexPage = 1
        addToast({
          type: 'info',
          title: 'There is no more pages before',
          description:
            'first page',
      });
      } else {
        //indexPage = indexPage
        currentPage--;
      }

      setPage(indexPage);
      
      const book = formRef.current?.getFieldValue('book');
  
      api.get(`/v1/volumes?q=${book}&orderBy=newest&maxResults=10&startIndex=${indexPage}`, {
      })
      .then((response) => {
        setBookList(response.data.items);
        totalPages = response.data.totalItems;
      });

    },
    [setPage, setBookList, addToast],
  );
  /******************* **********************/
  const handleSubmit = useCallback(
    async (data: SigInFormData) => {

      indexPage = 1;
      totalPages = 0;
      currentPage = 1;
      
      try {
        formRef.current?.setErrors({}); // cleaning errors
        const schema = Yup.object().shape({
          book: Yup.string()
            .required('book name is madatory'),
        });
        // .min(6, 'must have at least 6 characters'),

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .get(`/v1/volumes?q=${data.book}&orderBy=newest&maxResults=10&startIndex=${indexPage}`, {
          })
          .then((response) => {
            setBookList(response.data.items);
            totalPages = response.data.totalItems;
            setTotalPage(totalPages);
            setLoadingPage(true);
            
          }).catch((error) => {
            setLoadingPage(false);
            
            setBookList(bookList);
            totalPages = 0;
            addToast({
              type: 'error',
              title: 'error',
              description:
                'An error occured while your were searching for some book',
            });
          }
          ).finally(() =>{
            setLoadingPage(false);
            
            setTotalPage(totalPages);
          })
          ;
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
          return;
        }

        await addToast({
          type: 'error',
          title: 'error',
          description:
            'An error occured while your were searching for some book',
        });
      }
    },
    [setBookList, addToast, setTotalPage, bookList],
  );
  /******************* **********************/
   useEffect(() => {
    formRef.current?.setFieldValue('book', 'Harry Potter');
    api.get(`/v1/volumes?q=harry potter&orderBy=newest&maxResults=10&startIndex=${indexPage}`, {
      })
      .then((response) => {
        setBookList(response.data.items);
        totalPages = response.data.totalItems;
        setTotalPage(response.data.totalItems);
      })
      .finally(() =>{
        setLoadingPage(false);
        
        setTotalPage(totalPages);
      })
      ;
  },
  [setBookList, setTotalPage]);

  return (
    <Background>
      <Container>
      
      <Content>
      <AnimationContainer>
        <h1>Book Search</h1>
        <img src={logoImg} width="15%" alt="Library" />
        
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
        >
          <Input icon={FiBookOpen} name="book" placeholder="Book" />
          <Button type="submit">Search</Button>
          
          
          <Button onClick={previousPage} type="button">previous</Button>
          
          <Button onClick={nextPage} type="button">next</Button>
          
        </Form>
        </AnimationContainer>  
      </Content>

      <Content>
      <AnimationContainer>
        <>
          { totalPages > 0 ? paginationShow = true : paginationShow = false }
          <Pagination 
          isVisible={paginationShow}
          content="Page"
          currentPage={indexPage === 1 
            ? indexPage 
            : currentPage
            }
          totalPage={ Math.floor(totalPages/10) }
          totalRecords={totalPages}
          />    

          {(totalPages > 0 && (!loadingPage)) ?

            <ScrollContent>
            {bookList.map(book => (
              <ListContent
              id={book.id}
              title={book.volumeInfo.title}
              categories={book.volumeInfo.categories}
              description={book.volumeInfo.description}
              link={book.volumeInfo.previewLink}
              imgUrl={book.volumeInfo.imageLinks && book.volumeInfo.imageLinks.smallThumbnail }
              >
              </ListContent>
            ))}
            </ScrollContent>
          :
            loadingPage ?
              "Loading..."
            :
              `no books found for ${formRef.current?.getFieldValue('book')}`
          
          }
        </>
      </AnimationContainer>  
      </Content>
              
      </Container>
    </Background>
  );
};

export default Search;